import React, { useEffect, useRef } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import './richTextEditor.css';

// 5 popüler font seçeneği — sistem fontları, harici yükleme gerektirmiyor.
const Font = Quill.import('formats/font');
Font.whitelist = ['sans-serif', 'serif', 'monospace', 'verdana', 'comic-sans'];
Quill.register(Font, true);

const TOOLBAR_OPTIONS = [
    [{ header: [1, 2, false] }],
    [{ font: Font.whitelist }],
    ['bold', 'italic', 'underline'],
    [{ align: ['', 'center', 'right'] }],
];

// ~10 satırlık textarea gibi görünmesi için editör alanına doğrudan (satır içi)
// stil veriliyor — bu, herhangi bir CSS dosyasının yüklenme sırasından etkilenmez.
const EDITOR_HEIGHT_PX = 250;

// "questionText" gibi state'ler bu projede genelde useState([]) ile (boş DİZİ olarak)
// başlatılıyor; veri asenkron gelene kadar defaultValue string değil dizi olabiliyor.
// Quill'e string olmayan bir değer geçmek "cannot retain a string" hatasına yol açıyordu.
const isUsableHtmlString = (value) => typeof value === 'string' && value.trim() !== '';

// Quill kontrolsüz (uncontrolled) çalışıyor; gizli bir <textarea id={id}> ile senkron
// tutuluyor ki sayfadaki mevcut "document.getElementById(id).value" okuyan tüm submit
// kodları (handleQuestion, handletype* vb.) hiç değişmeden çalışmaya devam etsin.
const RichTextEditor = ({ id = 'questionText', label, defaultValue = '' }) => {
    const editorContainerRef = useRef(null);
    const quillRef = useRef(null);
    const textareaRef = useRef(null);
    const userHasTypedRef = useRef(false);

    // Quill sadece bir kez oluşturuluyor.
    useEffect(() => {
        if (quillRef.current || !editorContainerRef.current) return;

        const quill = new Quill(editorContainerRef.current, {
            theme: 'snow',
            modules: { toolbar: TOOLBAR_OPTIONS },
        });
        quillRef.current = quill;

        // Sabit yükseklik + taşınca dikey kaydırma — satır içi stil olarak veriliyor.
        quill.root.style.height = `${EDITOR_HEIGHT_PX}px`;
        quill.root.style.maxHeight = `${EDITOR_HEIGHT_PX}px`;
        quill.root.style.overflowY = 'auto';

        if (isUsableHtmlString(defaultValue)) {
            quill.clipboard.dangerouslyPasteHTML(defaultValue);
        }
        if (textareaRef.current) {
            textareaRef.current.value = isUsableHtmlString(defaultValue) ? defaultValue : '';
        }

        quill.on('text-change', (delta, oldDelta, source) => {
            if (source === 'user') userHasTypedRef.current = true;
            if (textareaRef.current) {
                textareaRef.current.value = quill.root.innerHTML;
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Düzenleme sayfalarında mevcut soru metni props üzerinden asenkron (bir tık sonra)
    // geliyor — editör zaten oluşmuş olsa bile içeriği o an güncelleyip yansıtıyoruz.
    // Kullanıcı bu arada yazmaya başladıysa üzerine yazmıyoruz.
    useEffect(() => {
        if (!quillRef.current || userHasTypedRef.current || !isUsableHtmlString(defaultValue)) return;
        quillRef.current.clipboard.dangerouslyPasteHTML(defaultValue);
        if (textareaRef.current) {
            textareaRef.current.value = defaultValue;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultValue]);

    return (
        <div className="mb-3">
            {label && <label htmlFor={id} className="form-label"><b>{label}</b></label>}
            <div className="rich-text-editor-wrapper">
                <div ref={editorContainerRef} className="rich-text-editor-surface" />
            </div>
            <textarea id={id} ref={textareaRef} defaultValue={isUsableHtmlString(defaultValue) ? defaultValue : ''} style={{ display: 'none' }} readOnly />
        </div>
    );
};

export default RichTextEditor;
